import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function OperaTrainingCenter() {
    const [isAnimated, setIsAnimated] = useState(false);
    const sectionRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastScrollY = useRef(0);
    const hasBeenAnimated = useRef(false);

    // Check if element is centered in the viewport
    const isCenteredInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        
        // Calculate the center of the element
        const elementCenterY = rect.top + (rect.height / 2);
        const elementCenterX = rect.left + (rect.width / 2);
        
        // Calculate the center of the viewport
        const viewportCenterY = windowHeight / 2;
        const viewportCenterX = windowWidth / 2;
        
        // Check if element center is within 100px of viewport center
        const isVerticallyCentered = Math.abs(elementCenterY - viewportCenterY) < 100;
        const isHorizontallyCentered = Math.abs(elementCenterX - viewportCenterX) < 100;
        
        return isVerticallyCentered && isHorizontallyCentered;
    };

    // Update animation based on scroll position
    const updateAnimation = () => {
        if (!sectionRef.current) return;

        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > lastScrollY.current;
        
        // Update last scroll position
        lastScrollY.current = currentScrollY;

        // Check if section is centered (for adding animation)
        const isCentered = isCenteredInViewport(sectionRef.current);

        // Add animation when centered and scrolling down (keep current logic)
        if (isCentered && isScrollingDown && !hasBeenAnimated.current) {
            setIsAnimated(true);
            hasBeenAnimated.current = true;
        }

        // Remove animation using Why Choose Us section logic
        if (hasBeenAnimated.current) {
            const section = sectionRef.current;
            const sectionTop = section.offsetTop;
            const sectionHeight = section.offsetHeight;
            const windowTop = window.scrollY;
            const windowHeight = window.innerHeight;

            // Calculate how much of the section is visible (same as Why Choose Us)
            const sectionVisible = windowTop + windowHeight - sectionTop;
            const progress = Math.max(
                0,
                Math.min(1, sectionVisible / (sectionHeight * 0.6))
            );

            // Remove animation when less than 60% visible (same as Why Choose Us)
            if (progress < 1) {
                setIsAnimated(false);
                hasBeenAnimated.current = false;
            }
        }
    };

    // Throttled scroll handler
    const throttledUpdate = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(updateAnimation);
    };

    useEffect(() => {
        // Initialize scroll position
        lastScrollY.current = window.scrollY;
        
        window.addEventListener("scroll", throttledUpdate);

        // Initial check
        updateAnimation();

        return () => {
            window.removeEventListener("scroll", throttledUpdate);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <section
            id="opera-training-section"
            ref={sectionRef}
            className={isAnimated ? "animate" : ""}
        >
            <div className="opera-container">
                <div className="opera-right">
                    <div className="opera-info">
                        <h2 className="section-title">
                            India&apos;s Most Premier Opera Training Center
                        </h2>
                        <p>
                            Home of India&apos;s First Ultra Soprano Opera Singer Harshita
                            School of Music is proud to be India&apos;s most premier opera
                            training center. Founded by Harshita Rewadia, internationally
                            known as the Opera Queen of India, first one to break glass by
                            voice, fame of India&apos;s Got Talent 2023, our academy brings
                            world-class opera training to India.
                        </p>

                        <div className="opera-features">
                            <div className="opera-feature">
                                <div className="feature-icon">
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M9 18V5l12-2v13" />
                                        <circle cx="6" cy="18" r="3" />
                                        <circle cx="18" cy="16" r="3" />
                                    </svg>
                                </div>
                                <span>Authentic European vocal techniques</span>
                            </div>

                            <div className="opera-feature">
                                <div className="feature-icon">
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M12 2v20M2 12h20" />
                                        <path d="m9 9 6 6" />
                                        <path d="m15 9-6 6" />
                                    </svg>
                                </div>
                                <span>Italian, German &amp; French repertoire training</span>
                            </div>

                            <div className="opera-feature">
                                <div className="feature-icon">
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22,4 12,14.01 9,11.01" />
                                    </svg>
                                </div>
                                <span>Stagecraft, performance, and resonance mastery</span>
                            </div>

                            <div className="opera-feature">
                                <div className="feature-icon">
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22,4 12,14.01 9,11.01" />
                                    </svg>
                                </div>
                                <span>
                                    Rare opportunity to train under one of the world&apos;s
                                    highest-pitched voices
                                </span>
                            </div>

                            <div className="opera-feature">
                                <div className="feature-icon">
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22,4 12,14.01 9,11.01" />
                                    </svg>
                                </div>
                                <span>Top class opera vocal coaches</span>
                            </div>
                        </div>
                        <p>
                            Opera is not just singing, it&apos;s the art of storytelling
                            through music. At HSM, we bring this rare art form to Indian soil
                            with unmatched excellence.
                        </p>
                        <span className="duration"><Link to="/opera-singing">Learn Opera</Link></span>
                    </div>
                </div>
            </div>
        </section>
    );
}
